/*
 * deferred-sections.js — near-viewport section loading (Section Rendering API)
 *
 * Pairs with snippets/section-defer.liquid. Each placeholder is
 *   <div class="section-deferred" data-section-deferred="<section id>" style="min-height:...">
 * sitting inside its normal <div id="shopify-section-<id>" class="shopify-section"> wrapper.
 *
 * Behaviour
 *  - IntersectionObserver with a 600px vertical margin: a section starts loading when the
 *    visitor is within ~one screen of it.
 *  - Everything that enters the margin in the same frame is batched into ONE request:
 *    <current page URL>?sections=a,b,c   (Shopify allows max 5 per request; we chunk.)
 *  - The response is JSON { "<id>": "<div id=shopify-section-<id> ...>…</div>" }. We replace
 *    the whole wrapper so classes from the section schema and section.id hooks are preserved.
 *  - Inline <script> tags inside fetched HTML do not run when inserted, so we re-create them.
 *    (category-carousel relies on this.) Alpine 3 initialises new DOM on its own.
 *  - A null entry (section missing/disabled) removes the placeholder so no blank gap remains.
 *  - Fires `section:deferred-loaded` on document with { id, el } for anything that wants a hook.
 *  - Never runs in the theme editor: section-defer.liquid does not emit placeholders there.
 */
(function () {
  'use strict';
  var SEL = '[data-section-deferred]';
  var MAX_PER_REQUEST = 5;
  var MARGIN = '600px 0px 600px 0px';

  var pending = [];
  var scheduled = false;

  function reexecuteScripts(root) {
    root.querySelectorAll('script').forEach(function (old) {
      var s = document.createElement('script');
      for (var i = 0; i < old.attributes.length; i++) {
        s.setAttribute(old.attributes[i].name, old.attributes[i].value);
      }
      s.textContent = old.textContent;
      old.parentNode.replaceChild(s, old);
    });
  }

  function swapIn(placeholder, id, html) {
    var wrapper = document.getElementById('shopify-section-' + id) || placeholder;
    if (!html) { wrapper.remove(); return; }
    var tpl = document.createElement('template');
    tpl.innerHTML = html.trim();
    var fresh = tpl.content.firstElementChild;
    if (!fresh) { wrapper.remove(); return; }
    wrapper.replaceWith(fresh);
    reexecuteScripts(fresh);
    document.dispatchEvent(new CustomEvent('section:deferred-loaded', { detail: { id: id, el: fresh } }));
  }

  function fetchBatch(els) {
    var ids = els.map(function (el) { return el.getAttribute('data-section-deferred'); });
    var url = new URL(window.location.href);
    url.searchParams.set('sections', ids.join(','));
    return fetch(url.toString(), { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('sections ' + r.status); return r.json(); })
      .then(function (data) {
        els.forEach(function (el, i) { swapIn(el, ids[i], data[ids[i]]); });
      })
      .catch(function (err) {
        // Leave the placeholder in place (min-height keeps layout stable) but stop announcing "busy".
        els.forEach(function (el) { el.removeAttribute('aria-busy'); el.setAttribute('data-defer-failed', ''); });
        if (window.console && console.warn) console.warn('[deferred-sections]', err);
      });
  }

  function flush() {
    scheduled = false;
    if (!pending.length) return;
    var batch = pending.splice(0, MAX_PER_REQUEST);
    fetchBatch(batch).then(function () { if (pending.length) schedule(); });
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    // rAF so everything that intersected in the same frame lands in one request.
    (window.requestAnimationFrame || setTimeout)(flush, 0);
  }

  function observe() {
    var els = document.querySelectorAll(SEL);
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) { pending = Array.prototype.slice.call(els); flush(); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        io.unobserve(en.target);
        pending.push(en.target);
        schedule();
      });
    }, { rootMargin: MARGIN });
    els.forEach(function (el) { io.observe(el); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observe);
  } else {
    observe();
  }
})();
